import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { publicAxios, authAxios, setAuthToken, clearAuthToken } from '@/lib/axios';
import { clearSentryUser, setSentryUser } from '@/lib/sentry';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { rememberLastUsedLoginMethod } from '@/lib/auth/lastUsedLoginMethod';
import { readCachedSessionUser } from '@/lib/auth/readCachedSessionUser';
import { resolveSessionLoadFailure } from '@/lib/auth/resolveSessionLoadFailure';
import {
  BACKEND_AVAILABLE_EVENT,
  BACKEND_UNAVAILABLE_EVENT,
} from '@/lib/auth/backendAvailability';

declare global {
  interface Window {
    posthog?: {
      identify: (distinctId: string, properties?: Record<string, unknown>) => void;
      reset: () => void;
      capture: (eventName: string, properties?: Record<string, unknown>) => void;
    };
    op?: (...args: unknown[]) => void;
    rybbit?: {
      identify: (userId: string | number) => void;
      event: (eventName: string, properties?: Record<string, unknown>) => void;
      clearUserId: () => void;
    };
    contyPixel?: {
      track: (eventName: string, properties?: Record<string, unknown>) => void;
    };
  }
}

interface User {
  id: number;
  name?: string | null;
  email: string;
  phone_number?: string;
  job_title?: string;
  target_objective?: string;
  onboarding_objective?: string | null;
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  createdAt: string;
  updatedAt: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isBackendUnavailable: boolean;
  /** /users/me or a login succeeded on this page (not just a cached user). */
  sessionVerified: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  updateProfile: (data: Partial<User>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refreshUser: () => Promise<User | null>;
  retrySession: () => Promise<void>;
  /** Re-probes the backend without unmounting the page (unlike retrySession). */
  recheckConnection: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isBackendUnavailable, setIsBackendUnavailable] = useState(false);
  const [sessionVerified, setSessionVerified] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  // Set up logout event listener
  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
      setIsBackendUnavailable(false);
      setSessionVerified(false);
      clearSentryUser();
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, []);

  useEffect(() => {
    const handleUnavailable = () => setIsBackendUnavailable(true);
    const handleAvailable = () => setIsBackendUnavailable(false);

    window.addEventListener(BACKEND_UNAVAILABLE_EVENT, handleUnavailable);
    window.addEventListener(BACKEND_AVAILABLE_EVENT, handleAvailable);
    return () => {
      window.removeEventListener(BACKEND_UNAVAILABLE_EVENT, handleUnavailable);
      window.removeEventListener(BACKEND_AVAILABLE_EVENT, handleAvailable);
    };
  }, []);

  // Fire Conty Pixel "lead" event on transition to authenticated user
  // (login, signup, OAuth callback, or already-logged-in session restore).
  const wasAuthenticatedRef = useRef(false);
  useEffect(() => {
    if (user && !wasAuthenticatedRef.current) {
      wasAuthenticatedRef.current = true;
      window.contyPixel?.track?.('lead');
    } else if (!user) {
      wasAuthenticatedRef.current = false;
    }
  }, [user]);

  // Label Sentry events and session replay as soon as we have an authenticated
  // user. Plan/subscription tags are enriched later from Sidebar (GraphQL).
  useEffect(() => {
    if (!user) return;
    setSentryUser({
      id: user.id,
      email: user.email,
      name: user.name,
      jobTitle: user.job_title,
      objective: user.onboarding_objective || user.target_objective,
      createdAt: user.createdAt,
    });
  }, [user]);

  const restoreSession = useCallback(async () => {
    const token = localStorage.getItem('token');

    // Load the user whenever a token is present. SSO (src/pages/Sso.tsx) stores
    // only the token, not a cached `user`, so requiring a cached user here left
    // SSO sessions unauthenticated and bounced them to /login. The token is the
    // source of truth; /users/me returns the user.
    if (!token) {
      setUser(null);
      setIsBackendUnavailable(false);
      setSessionVerified(false);
      return;
    }

    try {
      const response = await authAxios.get('/api/users/me');
      const userData = response.data;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setIsBackendUnavailable(false);
      setSessionVerified(true);
    } catch (error) {
      console.error('Failed to load user:', error);
      const result = resolveSessionLoadFailure({
        error,
        cachedUser: readCachedSessionUser(localStorage.getItem('user')),
      });

      if (result.clearSession) {
        clearAuthToken();
        setUser(null);
        setIsBackendUnavailable(false);
        setSessionVerified(false);
        return;
      }

      if (result.user) {
        setUser({
          ...result.user,
          createdAt: typeof result.user.createdAt === 'string' ? result.user.createdAt : '',
          updatedAt: typeof result.user.updatedAt === 'string' ? result.user.updatedAt : '',
        });
      }
      setIsBackendUnavailable(true);
    }
  }, []);

  // Load user data on mount
  useEffect(() => {
    let cancelled = false;

    const loadUser = async () => {
      await restoreSession();
      if (!cancelled) setIsLoading(false);
    };

    void loadUser();
    return () => {
      cancelled = true;
    };
  }, [restoreSession]);

  const retrySession = async () => {
    setIsLoading(true);
    try {
      await restoreSession();
    } finally {
      setIsLoading(false);
    }
  };

  const recheckConnection = useCallback(async () => {
    try {
      const response = await authAxios.get('/api/users/me');
      const userData = response.data;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setIsBackendUnavailable(false);
      setSessionVerified(true);
    } catch (error) {
      // authAxios already broadcast the outcome: still unreachable, or a real
      // 401 that logs the session out.
      console.error('Backend still unreachable:', error);
    }
  }, []);

  const login = async (email: string, password: string): Promise<User> => {
    try {
      const response = await publicAxios.post('/api/auth/local', {
        identifier: email,
        password: password,
      });

      const { jwt, user: userData } = response.data;
      setAuthToken(jwt);
      rememberLastUsedLoginMethod(localStorage, 'password');
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setIsBackendUnavailable(false);
      setSessionVerified(true);

      toast({
        title: t('auth.loginSuccess'),
        description: t('auth.welcomeBack'),
      });

      return userData;
    } catch (error: unknown) {
      const errorMessage = (error as any)?.response?.data?.error?.message || t('auth.loginFailed');
      toast({
        title: t('auth.loginFailed'),
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    }
  };

  const logout = () => {
    clearAuthToken();
    setUser(null);
    setIsBackendUnavailable(false);
    setSessionVerified(false);

    if (window.op) {
      window.op('clear');
    }

    if (window.rybbit) {
      window.rybbit.clearUserId();
    }

    // Reset PostHog user identification
    if (window.posthog) {
      window.posthog.reset();
    }

    // Clear Sentry user context
    clearSentryUser();

    toast({
      title: t('auth.loggedOut'),
      description: t('auth.loggedOutDescription'),
    });
  };

  const updateProfile = async (data: Partial<User>) => {
    try {
      const response = await authAxios.put('/api/users/me', {
        data
      });

      const updatedUser = response.data;
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      toast({
        title: t('auth.profileUpdated'),
        description: t('auth.profileUpdatedDescription'),
      });
    } catch (error: unknown) {
      const errorMessage = (error as any)?.response?.data?.error?.message || t('auth.profileUpdateError');
      toast({
        title: t('auth.profileUpdateFailed'),
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await authAxios.post('/api/auth/change-password', {
        currentPassword,
        password: newPassword,
        passwordConfirmation: newPassword,
      });

      toast({
        title: t('auth.passwordChanged'),
        description: t('auth.passwordChangedDescription'),
      });
    } catch (error: unknown) {
      const errorMessage = (error as any)?.response?.data?.error?.message || t('auth.passwordChangeError');
      toast({
        title: t('auth.passwordChangeError'),
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    }
  };

  const refreshUser = async (): Promise<User | null> => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    try {
      const response = await authAxios.get('/api/users/me');
      const userData = response.data;
      if (localStorage.getItem('token') !== token) return null;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setIsBackendUnavailable(false);
      setSessionVerified(true);
      return userData;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isBackendUnavailable,
    sessionVerified,
    login,
    logout,
    updateProfile,
    changePassword,
    refreshUser,
    retrySession,
    recheckConnection,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
